import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Container, Grid, Button, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../../ThemeContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import GridViewIcon from '@mui/icons-material/GridView';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import ViewAgendaRoundedIcon from '@mui/icons-material/ViewAgendaRounded';

// Icons
import SensorsIcon from '@mui/icons-material/Sensors';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VideoSettingsIcon from '@mui/icons-material/VideoSettings';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import TheaterComedyRoundedIcon from '@mui/icons-material/TheaterComedyRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import MonetizationOnRoundedIcon from '@mui/icons-material/MonetizationOnRounded';
import imgDistribution from '../../../assets/heroLady.png';
import imgFestivals from '../../../assets/about.png';

// Images
import imgLive from '../../../assets/ChatGPT Image Aug 24, 2026, 01_49_52 PM.png';
import imgBusiness from '../../../assets/ChatGPT Image Aug 24, 2026, 01_52_21 PM.png';
import imgMarketing from '../../../assets/ChatGPT Image Aug 24, 2026, 01_54_39 PM.png';
import imgAssistant from '../../../assets/ChatGPT Image Aug 24, 2026, 01_57_04 PM.png';
import imgEditor from '../../../assets/ChatGPT Image Aug 24, 2026, 02_00_10 PM.png';
import imgFilters from '../../../assets/06_ai_filters.png';
import imgEffects from '../../../assets/movies.png';
import imgShootingLocation from '../../../assets/shootinglocation.png';
import imgAuditions from '../../../assets/audition.png';
import imgJobs from '../../../assets/marketPlace.png';

const INDUSTRY_CATEGORIES = [
  { id: 'all', label: 'All Industries (12)', icon: <AutoAwesomeIcon sx={{ fontSize: '1rem' }} /> },
  { id: 'locations', label: 'Shooting Locations', targetIdx: 7, icon: <LocationOnRoundedIcon sx={{ fontSize: '1rem' }} />, color: '#10b981' },
  { id: 'auditions', label: 'Auditions & Casting', targetIdx: 8, icon: <TheaterComedyRoundedIcon sx={{ fontSize: '1rem' }} />, color: '#f43f5e' },
  { id: 'jobs', label: 'Jobs & Crew Hiring', targetIdx: 9, icon: <WorkRoundedIcon sx={{ fontSize: '1rem' }} />, color: '#06b6d4' },
  { id: 'ai', label: 'AI & Post-Production', targetIdx: 3, icon: <SmartToyIcon sx={{ fontSize: '1rem' }} />, color: '#8a2be2' },
  { id: 'live', label: 'Live & Enterprise', targetIdx: 0, icon: <SensorsIcon sx={{ fontSize: '1rem' }} />, color: '#e91e63' },
];

const INDUSTRY_DATA = [
  {
    id: 1,
    category: 'Live & Broadcast',
    categoryKey: 'live',
    title: 'Live',
    subtitle: 'Ultra-Low Latency 4K Broadcast & Interactive Chat',
    kicker: 'LIVE BROADCAST & STREAMING',
    desc: 'Go live, engage your audience, and stream your cinema content in real time with ultra-low latency and live reactions.',
    icon: <SensorsIcon />,
    badge: 'LIVE BROADCAST',
    bullets: ['Live Streaming 4K HDR 60FPS', 'Real-Time Viewer Polls & Q&A', 'Interactive Live Chat & Emotes', 'Multi-Platform Simultaneous Broadcast'],
    btnText: 'Explore Live',
    themeColor: '#e91e63',
    glowColor: 'rgba(233, 30, 99, 0.45)',
    bgGradient: 'linear-gradient(135deg, rgba(233,30,99,0.15) 0%, rgba(255,255,255,0) 100%)',
    image: imgLive,
    route: '/industry/live',
    metrics: [
      { label: 'Active Viewers', value: '18.4K+' },
      { label: 'Latency', value: '< 18ms' },
      { label: 'Quality', value: '4K DCI 60FPS' },
    ],
    showcasePills: ['🔴 1080p / 4K HLS', '💬 Live Polls', '🎙️ Dolby Audio'],
  },
  {
    id: 2,
    category: 'Enterprise & Media',
    categoryKey: 'live',
    title: 'Business',
    subtitle: 'Enterprise Brand Production & Corporate Media',
    kicker: 'ENTERPRISE & BRAND PRODUCTION',
    desc: 'Create high-impact corporate videos, product demos, and brand presentations that grow your business.',
    icon: <BusinessCenterIcon />,
    badge: 'ENTERPRISE',
    bullets: ['Corporate Video Production', 'High-Impact Product Demos', 'Interactive Brand Presentations', 'Secure 256-Bit DRM Internal Comms'],
    btnText: 'Explore Business',
    themeColor: '#2196f3',
    glowColor: 'rgba(33, 150, 243, 0.45)',
    bgGradient: 'linear-gradient(135deg, rgba(33,150,243,0.15) 0%, rgba(255,255,255,0) 100%)',
    image: imgBusiness,
    route: '/industry/business',
    metrics: [
      { label: 'Enterprise ROI', value: '3.4x' },
      { label: 'Security', value: '256-Bit DRM' },
      { label: 'Global CDN', value: '100+ Nodes' },
    ],
    showcasePills: ['💼 Pitch Decks', '📊 Analytics HUD', '🔒 DRM Protection'],
  },
  {
    id: 3,
    category: 'Marketplace & Rights',
    categoryKey: 'live',
    title: 'Global Marketing',
    subtitle: 'Film Rights Licensing & Worldwide PR Distribution',
    kicker: 'FILM INDUSTRY MARKETPLACE',
    desc: 'Promote, license, and collaborate with creators, buyers, distributors, and production crews worldwide.',
    icon: <ShoppingCartIcon />,
    badge: 'MARKETPLACE',
    bullets: ['Film Rights Licensing & Bins', 'Talent & Crew Verified Directory', 'Cinema Equipment Rental Hub', 'Direct-to-Buyer Distribution'],
    btnText: 'Explore Marketplace',
    themeColor: '#8a2be2',
    glowColor: 'rgba(138, 43, 226, 0.45)',
    bgGradient: 'linear-gradient(135deg, rgba(138,43,226,0.15) 0%, rgba(255,255,255,0) 100%)',
    image: imgMarketing,
    route: '/industry/global-marketing',
    metrics: [
      { label: 'Projects Listed', value: '2,840+' },
      { label: 'Deal Volume', value: '$14.2M' },
      { label: 'Verified Buyers', value: '100+ Nations' },
    ],
    showcasePills: ['🌐 Global Rights', '🤝 Smart Contracts', '🎬 Trailer Screening'],
  },
  {
    id: 4,
    category: 'AI & Automation',
    categoryKey: 'ai',
    title: 'AI Assistant',
    subtitle: 'Screenplay Breakdowns & Creative Story Copilot',
    kicker: 'NEURAL SCRIPT & STORY COPILOT',
    desc: 'Your smart creative AI assistant to generate screenplay breakdowns, character specs, and auto captions.',
    icon: <SmartToyIcon />,
    badge: 'AI COPILOT',
    bullets: ['AI Screenplay & Story Assistance', 'Real-Time Multilingual Auto Captions', 'Automated Character Casting Radar', 'Shotlist & Storyboard Tokenization'],
    btnText: 'Explore AI Assistant',
    themeColor: '#673ab7',
    glowColor: 'rgba(103, 58, 183, 0.45)',
    bgGradient: 'linear-gradient(135deg, rgba(103,58,183,0.15) 0%, rgba(255,255,255,0) 100%)',
    image: imgAssistant,
    route: '/industry/ai-assistant',
    metrics: [
      { label: 'Generation Speed', value: '< 2.1s' },
      { label: 'Accuracy', value: '99.2%' },
      { label: 'Languages', value: '45+ DCI' },
    ],
    showcasePills: ['🤖 Script Breakdown', '📝 Auto Dialogue', '⚡ 1-Tap Tokenizer'],
  },
  {
    id: 5,
    category: 'Post-Production & NLE',
    categoryKey: 'ai',
    title: 'Own Media Editor',
    subtitle: 'Cloud 4K Multi-Track Video Timeline & Stem Audio',
    kicker: 'CLOUD MULTI-TRACK NLE',
    desc: 'Edit 4K video, audio stems, and visual effects directly in the cloud on a fluid multi-track timeline.',
    icon: <VideoSettingsIcon />,
    badge: 'CLOUD NLE',
    bullets: ['4K Multi-Track Video Timeline', 'Integrated Audio Stem Equalizer', 'Real-Time 3D LUT Color Grading', 'Collaborative Cloud Project Bins'],
    btnText: 'Open Media Editor',
    themeColor: '#ff9800',
    glowColor: 'rgba(255, 152, 0, 0.45)',
    bgGradient: 'linear-gradient(135deg, rgba(255,152,0,0.15) 0%, rgba(255,255,255,0) 100%)',
    image: imgEditor,
    route: '/industry/own-media-editor',
    metrics: [
      { label: 'Timeline Tracks', value: '32-Tracks' },
      { label: 'Framerate', value: '120 FPS' },
      { label: 'Colorist Grading', value: '3D LUTs' },
    ],
    showcasePills: ['✂️ Speed Ramping', '🎚️ Multi-Track Audio', '🎞️ 4K Export'],
  },
  {
    id: 6,
    category: 'Color & Stock LUTs',
    categoryKey: 'ai',
    title: 'AI Filters',
    subtitle: 'Hollywood 35mm Film Stocks & Neural Relighting',
    kicker: 'NEURAL 35MM GRAIN & COLOR',
    desc: 'Transform raw camera footage with neural Hollywood film stock emulations and AI portrait lighting.',
    icon: <AutoAwesomeIcon />,
    badge: 'AI LUTs',
    bullets: ['Kodak 500T Stock Emulation', 'Neural Portrait Relighting', 'Teal & Orange Cinema Grade', 'Dynamic Grain & Anamorphic Blur'],
    btnText: 'Explore AI Filters',
    themeColor: '#00bcd4',
    glowColor: 'rgba(0, 188, 212, 0.45)',
    bgGradient: 'linear-gradient(135deg, rgba(0,188,212,0.15) 0%, rgba(255,255,255,0) 100%)',
    image: imgFilters,
    route: '/industry/ai-filters',
    metrics: [
      { label: 'Grading Presets', value: '150+ Styles' },
      { label: 'Render Latency', value: 'Zero Latency' },
      { label: 'Color Accuracy', value: 'Rec.709 & DCI-P3' },
    ],
    showcasePills: ['🎨 Bleach Bypass', '🎞️ 35mm Grain', '☀️ Neural Relight'],
  },
  {
    id: 7,
    category: 'VFX & Visual Effects',
    categoryKey: 'ai',
    title: 'Effects',
    subtitle: 'Optical Anamorphic Flares & 3D Motion Graphics',
    kicker: 'CINEMATIC VFX & COMPOSITING',
    desc: 'Composite optical anamorphic lens flares, dynamic particle simulations, and 3D motion graphics.',
    icon: <AutoFixHighIcon />,
    badge: 'STUDIO VFX',
    bullets: ['Anamorphic Optical Flares', 'Atmospheric Smoke & Sparks', '3D Motion Graphics & Typography', 'Cinematic Sound Synchronizer'],
    btnText: 'Explore Effects',
    themeColor: '#1976d2',
    glowColor: 'rgba(25, 118, 210, 0.45)',
    bgGradient: 'linear-gradient(135deg, rgba(25,118,210,0.15) 0%, rgba(255,255,255,0) 100%)',
    image: imgEffects,
    route: '/industry/effects',
    metrics: [
      { label: 'VFX Presets', value: '250+ Assets' },
      { label: 'Engine', value: 'WebGL 3D' },
      { label: 'Aspect Ratio', value: '2.39:1 DCI' },
    ],
    showcasePills: ['✨ Anamorphic Flare', '💥 Energy Shocks', '🎬 Cinematic Title'],
  },
  {
    id: 8,
    category: 'Shooting Locations',
    categoryKey: 'locations',
    title: 'Shooting Locations',
    subtitle: 'Soundstages, Sets & Scenic Location Scouting',
    kicker: 'LOCATIONS & STUDIO SCOUTING',
    desc: 'Scout verified soundstages, exotic outdoor sets, and iconic filming locations with 360° virtual tours and instant municipal permits.',
    icon: <LocationOnRoundedIcon />,
    badge: 'LOCATIONS & SETS',
    bullets: ['10,000+ Verified Global Studios & Sets', 'Automated Municipal & Drone Filming Permits', '360° Photogrammetry & Virtual Site Tours', 'Direct Soundstage & Venue Booking'],
    btnText: 'Explore Locations',
    themeColor: '#10b981',
    glowColor: 'rgba(16, 185, 129, 0.45)',
    bgGradient: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(255,255,255,0) 100%)',
    image: imgShootingLocation,
    route: '/industry/shooting-locations',
    metrics: [
      { label: 'Scouted Sets', value: '10,400+' },
      { label: 'Permit Clearance', value: '< 24 Hours' },
      { label: 'Panoramic VR', value: '360° 8K' },
    ],
    showcasePills: ['📍 360° Scouting', '📜 Instant Permits', '🏛️ Soundstage Bins', '☀️ Solar HUD'],
  },
  {
    id: 9,
    category: 'Auditions & Casting',
    categoryKey: 'auditions',
    title: 'Auditions',
    subtitle: 'Casting Calls, Self-Tapes & Voice Talents',
    kicker: 'CASTING CALLS & DIGITAL AUDITIONS',
    desc: 'Discover breakout acting talent, post global casting notices, and manage digital video self-tapes with AI role-fit matching.',
    icon: <TheaterComedyRoundedIcon />,
    badge: 'CASTING & TALENT',
    bullets: ['Direct Video Self-Tape Submissions', 'Real-Time AI Role-Fit & Accent Analysis', 'Automated Casting Slate & Script Reader', 'Virtual Table Reads & 1-Tap Callbacks'],
    btnText: 'Explore Auditions',
    themeColor: '#f43f5e',
    glowColor: 'rgba(244, 63, 94, 0.45)',
    bgGradient: 'linear-gradient(135deg, rgba(244,63,94,0.15) 0%, rgba(255,255,255,0) 100%)',
    image: imgAuditions,
    route: '/industry/auditions',
    metrics: [
      { label: 'Active Casting Calls', value: '1,850+' },
      { label: 'Callback Rate', value: '94%' },
      { label: 'Global Cast', value: '50K+ Actors' },
    ],
    showcasePills: ['🎭 Video Self-Tape', '🤖 AI Role Match', '📞 1-Tap Callback', '🎙️ Voice Casting'],
  },
  {
    id: 10,
    category: 'Jobs & Crew Hiring',
    categoryKey: 'jobs',
    title: 'Jobs & Crew',
    subtitle: 'Production Crew Hiring, Day Rates & Guild Careers',
    kicker: 'CREW HIRING & PRODUCTION CAREERS',
    desc: 'Hire certified cinematographers, sound recordists, gaffers, and editors with transparent day rates and escrow protection.',
    icon: <WorkRoundedIcon />,
    badge: 'CREW & JOBS',
    bullets: ['Verified Union & Indie Crew Directory', 'Milestone Escrow Day-Rate Payment Protection', 'Department Head, Gaffer & DP Matching', 'Live Call Sheet & Production Schedule Sync'],
    btnText: 'Explore Jobs',
    themeColor: '#06b6d4',
    glowColor: 'rgba(6, 182, 212, 0.45)',
    bgGradient: 'linear-gradient(135deg, rgba(6,182,212,0.15) 0%, rgba(255,255,255,0) 100%)',
    image: imgJobs,
    route: '/industry/jobs',
    metrics: [
      { label: 'Verified Crew', value: '42,000+' },
      { label: 'Payment Escrow', value: '100% Protected' },
      { label: 'Hiring Match Time', value: '< 2 Hours' },
    ],
    showcasePills: ['🎬 DP & Lighting Rigs', '💼 Guild Contracts', '⏰ Real-Time Callsheet', '🛡️ Escrow Pay'],
  },
  {
    id: 11,
    category: 'Film Festivals & Awards',
    categoryKey: 'festivals',
    title: 'Festival Circuit',
    subtitle: 'Global Laurels, Festival Submissions & Screenings',
    kicker: 'GLOBAL FESTIVALS & CINEMA CIRCUIT',
    desc: 'Submit to 500+ verified film festivals worldwide, track judge ratings, and showcase official laurels directly to distributors.',
    icon: <CampaignRoundedIcon />,
    badge: 'AWARDS & CIRCUIT',
    bullets: ['1-Tap Multi-Festival Submissions', 'Official Selection Laurels Badging', 'Judge & Curator Private Screener Rooms', 'Press Kit & Festival DCP Packaging'],
    btnText: 'Explore Festivals',
    themeColor: '#f59e0b',
    glowColor: 'rgba(245, 158, 11, 0.45)',
    bgGradient: 'linear-gradient(135deg, rgba(245,158,11,0.15) 0%, rgba(255,255,255,0) 100%)',
    image: imgFestivals,
    route: '/products',
    metrics: [
      { label: 'Active Festivals', value: '520+' },
      { label: 'Selection Rate', value: '78%' },
      { label: 'Laurel Badges', value: '10K+' },
    ],
    showcasePills: ['🏆 Gold Laurels', '🎟️ Screener HUD', '📰 EPK Distribution', '🌍 90+ Countries'],
  },
  {
    id: 12,
    category: 'Financing & OTT Sales',
    categoryKey: 'financing',
    title: 'Film Financing',
    subtitle: 'Co-Productions, Pre-Sales & OTT Distribution',
    kicker: 'FILM FINANCING & EQUITY SYNDICATION',
    desc: 'Connect with film funds, accredited co-producers, and global OTT buyers with transparent milestone smart contracts.',
    icon: <MonetizationOnRoundedIcon />,
    badge: 'INVEST & FINANCING',
    bullets: ['Co-Production Equity Syndication', 'Global OTT & Theatrical Pre-Sales', 'Milestone-Based Escrow Payouts', 'Tax Credit & Rebate Calculators'],
    btnText: 'Explore Financing',
    themeColor: '#8b5cf6',
    glowColor: 'rgba(139, 92, 246, 0.45)',
    bgGradient: 'linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(255,255,255,0) 100%)',
    image: imgDistribution,
    route: '/products',
    metrics: [
      { label: 'Funded Projects', value: '$84M+' },
      { label: 'Global OTT Buyers', value: '340+' },
      { label: 'Investor Match', value: '3.2x Faster' },
    ],
    showcasePills: ['💰 Equity Bins', '📜 Pre-Sale Deals', '🔒 Escrow Vault', '📈 ROI Telemetry'],
  },
];

export default function Purpose() {
  const { isDarkMode } = useTheme();
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // Uniform, auto-fitting grid card deck with zero blank space

  const activeData = INDUSTRY_DATA[activeSlideIndex] || INDUSTRY_DATA[0];

  // Auto-slide every 4.8 seconds unless paused by hover
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setActiveSlideIndex((prev) => (prev + 1) % INDUSTRY_DATA.length);
    }, 4800);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const handlePrev = useCallback(() => {
    setActiveSlideIndex((prev) => (prev === 0 ? INDUSTRY_DATA.length - 1 : prev - 1));
  }, []);

  const handleNext = useCallback(() => {
    setActiveSlideIndex((prev) => (prev + 1) % INDUSTRY_DATA.length);
  }, []);

  // Handle Category selection
  const handleCategorySelect = (cat) => {
    setSelectedCategory(cat.id);
    if (cat.targetIdx !== undefined) {
      setActiveSlideIndex(cat.targetIdx);
    }
  };

  // Compute displayed 4-card window for the 2x2 split grid
  const getDisplayedCards = () => {
    if (selectedCategory === 'locations') {
      // Shooting Locations prominent + related production sectors
      return [INDUSTRY_DATA[7], INDUSTRY_DATA[9], INDUSTRY_DATA[1], INDUSTRY_DATA[6]];
    }
    if (selectedCategory === 'auditions') {
      // Auditions prominent + related talent & broadcast sectors
      return [INDUSTRY_DATA[8], INDUSTRY_DATA[0], INDUSTRY_DATA[9], INDUSTRY_DATA[2]];
    }
    if (selectedCategory === 'jobs') {
      // Jobs & Crew prominent + related production & studio sectors
      return [INDUSTRY_DATA[9], INDUSTRY_DATA[7], INDUSTRY_DATA[1], INDUSTRY_DATA[4]];
    }
    if (selectedCategory === 'ai') {
      return [INDUSTRY_DATA[3], INDUSTRY_DATA[4], INDUSTRY_DATA[5], INDUSTRY_DATA[6]];
    }
    if (selectedCategory === 'live') {
      return [INDUSTRY_DATA[0], INDUSTRY_DATA[1], INDUSTRY_DATA[2], INDUSTRY_DATA[8]];
    }
    // 'all' category: sliding window containing active card
    const startIdx = activeSlideIndex >= 8 ? 6 : activeSlideIndex >= 4 ? 4 : 0;
    return INDUSTRY_DATA.slice(startIdx, startIdx + 4);
  };

  const displayedCards = getDisplayedCards();

  // Grid view filtered cards
  const gridCards = selectedCategory === 'all'
    ? INDUSTRY_DATA
    : selectedCategory === 'locations'
    ? [INDUSTRY_DATA[7], INDUSTRY_DATA[9], INDUSTRY_DATA[1], INDUSTRY_DATA[6]]
    : selectedCategory === 'auditions'
    ? [INDUSTRY_DATA[8], INDUSTRY_DATA[0], INDUSTRY_DATA[9], INDUSTRY_DATA[2]]
    : selectedCategory === 'jobs'
    ? [INDUSTRY_DATA[9], INDUSTRY_DATA[7], INDUSTRY_DATA[1], INDUSTRY_DATA[4]]
    : INDUSTRY_DATA.filter(item => item.categoryKey === selectedCategory);

  return (
    <Box sx={{ pt: { xs: 2.5, md: 3.5 }, pb: { xs: 6, md: 8 }, px: { xs: 2, md: 4 }, background: isDarkMode ? '#030712' : '#f8fafc', position: 'relative', overflow: 'hidden' }}>
      <Container maxWidth="xl">
        
        {/* Header Section with Live Status & Interactive Controls */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 3.5, gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.8 }}>
              <AutoAwesomeIcon sx={{ color: '#8a2be2', fontSize: '2rem' }} />
              <Typography variant="h3" fontWeight={900} sx={{ color: isDarkMode ? '#fff' : '#0f172a', fontSize: { xs: '2rem', md: '2.6rem' }, letterSpacing: '-0.02em' }}>
                Explore by <Box component="span" sx={{ background: 'linear-gradient(135deg, #8a2be2 0%, #a855f7 50%, #07a4f5 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'inline-block' }}>Industry</Box>
              </Typography>
            </Box>
            <Typography sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '1.02rem', ml: { xs: 0, sm: 5 }, maxWidth: '720px', lineHeight: 1.6 }}>
              Tailored high-performance creative suites, casting calls, equipment marketplaces, shooting locations, and crew careers for the film industry.
            </Typography>
          </Box>
          
          {/* Navigation & Mode Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, alignSelf: { xs: 'stretch', md: 'auto' }, justifyContent: { xs: 'space-between', md: 'flex-end' } }}>
            {/* Slide Pagination Indicator */}
            <Box
              sx={{
                px: 1.8,
                py: 0.6,
                borderRadius: '20px',
                background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
              }}
            >
              <Typography sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 800, color: activeData.themeColor }}>
                {String(activeSlideIndex + 1).padStart(2, '0')}
              </Typography>
              <Typography sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: isDarkMode ? '#64748b' : '#94a3b8' }}>
                / {String(INDUSTRY_DATA.length).padStart(2, '0')}
              </Typography>
            </Box>

            {/* Left Button */}
            <IconButton
              onClick={handlePrev}
              title="Previous Sector"
              sx={{
                background: isDarkMode ? '#1e293b' : '#fff',
                boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
                border: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
                '&:hover': { background: isDarkMode ? '#334155' : '#f1f5f9', transform: 'scale(1.05)' },
                transition: 'all 0.2s ease',
              }}
            >
              <KeyboardArrowLeftIcon sx={{ color: isDarkMode ? '#fff' : '#0f172a' }} />
            </IconButton>

            {/* Right Button */}
            <IconButton
              onClick={handleNext}
              title="Next Sector"
              sx={{
                background: isDarkMode ? '#1e293b' : '#fff',
                boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
                border: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
                '&:hover': { background: isDarkMode ? '#334155' : '#f1f5f9', transform: 'scale(1.05)' },
                transition: 'all 0.2s ease',
              }}
            >
              <KeyboardArrowRightIcon sx={{ color: isDarkMode ? '#fff' : '#0f172a' }} />
            </IconButton>

            {/* Grid / Split Mode Toggle */}
            <IconButton
              onClick={() => setViewMode((prev) => (prev === 'split' ? 'grid' : 'split'))}
              title={viewMode === 'split' ? 'Show All 10 in Grid' : 'Show Split Cinema Showcase'}
              sx={{
                background: viewMode === 'grid' ? activeData.themeColor : isDarkMode ? '#1e293b' : '#fff',
                color: viewMode === 'grid' ? '#fff' : isDarkMode ? '#fff' : '#0f172a',
                boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
                border: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
                '&:hover': { opacity: 0.9, transform: 'scale(1.05)' },
                transition: 'all 0.2s ease',
              }}
            >
              {viewMode === 'split' ? <GridViewIcon sx={{ fontSize: '1.2rem' }} /> : <ViewAgendaRoundedIcon sx={{ fontSize: '1.2rem' }} />}
            </IconButton>
          </Box>
        </Box>

        {/* 1. Primary Data Categories Bar (Targeting Shooting Locations, Auditions, Jobs & Crew) */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.2,
            overflowX: 'auto',
            pb: 1.5,
            mb: 2,
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {INDUSTRY_CATEGORIES.map((cat) => {
            const isCatActive = selectedCategory === cat.id;
            const catColor = cat.color || '#8a2be2';
            return (
              <Box
                key={cat.id}
                onClick={() => handleCategorySelect(cat)}
                component={motion.div}
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.97 }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2.2,
                  py: 1,
                  borderRadius: '16px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  background: isCatActive
                    ? `linear-gradient(135deg, ${catColor} 0%, ${catColor}dd 100%)`
                    : isDarkMode
                    ? 'rgba(255, 255, 255, 0.04)'
                    : 'rgba(255, 255, 255, 0.9)',
                  border: isCatActive
                    ? `2px solid ${catColor}`
                    : isDarkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                  color: isCatActive ? '#ffffff' : isDarkMode ? '#cbd5e1' : '#334155',
                  boxShadow: isCatActive
                    ? `0 10px 25px ${catColor}50, 0 0 15px ${catColor}30`
                    : isDarkMode
                    ? 'none'
                    : '0 4px 12px rgba(0,0,0,0.03)',
                  transition: 'all 0.25s ease',
                  fontSize: '0.84rem',
                  fontWeight: isCatActive ? 800 : 600,
                }}
              >
                <Box
                  component={motion.div}
                  animate={isCatActive ? { scale: [1, 1.2, 1], rotate: [0, 6, -6, 0] } : {}}
                  transition={{ duration: 2.5, repeat: Infinity }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '1.1rem',
                    color: isCatActive ? '#ffffff' : catColor,
                  }}
                >
                  {cat.icon}
                </Box>
                <Typography sx={{ fontSize: '0.84rem', fontWeight: isCatActive ? 800 : 600 }}>
                  {cat.label}
                </Typography>
                {isCatActive && (
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: '#ffffff',
                      boxShadow: '0 0 8px #ffffff',
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>

        {/* 2. Interactive Sector Quick-Filter Chip Bar for all 10 Cinema Industries */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            pb: 2,
            mb: 3.5,
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {INDUSTRY_DATA.map((item, idx) => {
            const isSelected = activeSlideIndex === idx;
            return (
              <Box
                key={item.id}
                onClick={() => {
                  setActiveSlideIndex(idx);
                  // Highlight and smoothly scroll to the chosen cinema card without collapsing other cards into dead zones
                  const el = document.getElementById('cinema-card-' + item.id);
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                  }
                }}
                component={motion.div}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.96 }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.9,
                  px: 1.8,
                  py: 0.65,
                  borderRadius: '50px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  background: isSelected
                    ? item.themeColor
                    : isDarkMode
                    ? 'rgba(255, 255, 255, 0.03)'
                    : 'rgba(0, 0, 0, 0.03)',
                  border: isSelected
                    ? `1.5px solid ${item.themeColor}`
                    : isDarkMode
                    ? '1px solid rgba(255, 255, 255, 0.07)'
                    : '1px solid rgba(0, 0, 0, 0.06)',
                  color: isSelected ? '#ffffff' : isDarkMode ? '#94a3b8' : '#475569',
                  boxShadow: isSelected ? `0 6px 20px ${item.glowColor}` : 'none',
                  transition: 'all 0.25s ease',
                  fontSize: '0.8rem',
                  fontWeight: isSelected ? 800 : 600,
                }}
              >
                <Box
                  component={motion.div}
                  animate={isSelected ? { rotate: [0, 8, -8, 0], scale: [1, 1.15, 1] } : {}}
                  transition={{ duration: 2, repeat: Infinity }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '1rem',
                    color: isSelected ? '#fff' : item.themeColor,
                  }}
                >
                  {item.icon}
                </Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: isSelected ? 800 : 600 }}>
                  {item.title}
                </Typography>
                {isSelected && (
                  <Box
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      background: '#fff',
                      boxShadow: '0 0 6px #fff',
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>

        {/* View Mode 1: Split Cinema Studio Showcase */}
        {viewMode === 'split' ? (
          <Grid container spacing={3.5} alignItems="stretch">
            
            {/* Left Column: 4 Selectable Industry Cards dynamically synchronized with active selection */}
            <Grid size={{ xs: 12 }} lg={6.8}>
              <Grid container spacing={2.5}>
                {displayedCards.map((data) => {
                  const actualIdx = INDUSTRY_DATA.findIndex((it) => it.id === data.id);
                  const isSelected = activeSlideIndex === actualIdx;
                  return (
                    <Grid size={{ xs: 12 }} sm={6} key={data.id}>
                      <Box
                        onClick={() => setActiveSlideIndex(actualIdx)}
                        component={motion.div}
                        whileHover={{ y: -6, scale: 1.01 }}
                        transition={{ duration: 0.25 }}
                        sx={{
                          cursor: 'pointer',
                          height: '100%',
                        }}
                      >
                        <IndustryCard data={data} isDarkMode={isDarkMode} isSelected={isSelected} />
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Quick Dots to Access all 10 items */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2.5, px: 1, flexWrap: 'wrap', gap: 1.5 }}>
                <Typography sx={{ fontSize: '0.76rem', color: isDarkMode ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
                  Showing active cinema suites (12 Total). Select any dot or chip above:
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.9, alignItems: 'center' }}>
                  {INDUSTRY_DATA.map((item, i) => (
                    <Box
                      key={item.id}
                      onClick={() => setActiveSlideIndex(i)}
                      title={item.title}
                      sx={{
                        width: activeSlideIndex === i ? 26 : 8,
                        height: 8,
                        borderRadius: '4px',
                        background: activeSlideIndex === i ? item.themeColor : isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                        '&:hover': { background: item.themeColor, transform: 'scale(1.2)' },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Right Column: Fills the Free Extra Gap with the Animated Live Studio Stage Deck */}
            <Grid size={{ xs: 12 }} lg={5.2}>
              <Box
                onMouseEnter={() => setIsAutoPlaying(false)}
                onMouseLeave={() => setIsAutoPlaying(true)}
                sx={{
                  position: 'relative',
                  height: '100%',
                  minHeight: { xs: 480, lg: '100%' },
                  borderRadius: '28px',
                  background: isDarkMode
                    ? 'linear-gradient(145deg, #0d1527 0%, #070e1b 100%)'
                    : 'linear-gradient(145deg, #ffffff 0%, #f4f8ff 100%)',
                  border: isDarkMode
                    ? `1.5px solid ${activeData.themeColor}50`
                    : `1.5px solid ${activeData.themeColor}35`,
                  boxShadow: isDarkMode
                    ? `0 24px 60px rgba(0, 0, 0, 0.8), 0 0 45px ${activeData.glowColor}`
                    : `0 20px 50px rgba(15, 23, 42, 0.12), 0 0 35px ${activeData.glowColor}`,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  p: { xs: 3, md: 3.8 },
                  transition: 'border-color 0.4s ease, box-shadow 0.4s ease',
                }}
              >
                {/* Background Ambient Spotlight Glow */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-15%',
                    right: '-15%',
                    width: '320px',
                    height: '320px',
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${activeData.glowColor} 0%, transparent 70%)`,
                    pointerEvents: 'none',
                    filter: 'blur(30px)',
                  }}
                />

                {/* Top Deck HUD Header */}
                <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: activeData.themeColor,
                        boxShadow: `0 0 10px ${activeData.themeColor}`,
                        animation: 'hudPulse 1.2s infinite',
                        '@keyframes hudPulse': {
                          '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                          '50%': { transform: 'scale(1.4)', opacity: 0.4 },
                        },
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.72rem',
                        fontWeight: 800,
                        letterSpacing: '0.14em',
                        color: activeData.themeColor,
                        textTransform: 'uppercase',
                      }}
                    >
                      {activeData.kicker}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      px: 1.2,
                      py: 0.35,
                      borderRadius: '6px',
                      background: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
                      border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
                      fontFamily: 'monospace',
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      color: isDarkMode ? '#cbd5e1' : '#475569',
                    }}
                  >
                    4K DCI • ECOSYSTEM
                  </Box>
                </Box>

                {/* Center Dynamic Visual Stage with Smooth Crossfade */}
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 2,
                    height: { xs: 200, sm: 220, md: 240 },
                    borderRadius: '20px',
                    overflow: 'hidden',
                    my: 1,
                    boxShadow: '0 16px 40px rgba(0,0,0,0.3)',
                  }}
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeData.id}
                      initial={{ opacity: 0, scale: 0.95, y: 15 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 1.05, y: -15 }}
                      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                      style={{ width: '100%', height: '100%', position: 'relative' }}
                    >
                      <Box
                        component="img"
                        src={activeData.image}
                        alt={activeData.title}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: activeData.id >= 8 ? 'contain' : 'cover',
                          p: activeData.id >= 8 ? 2 : 0,
                          transform: activeData.id >= 8 ? 'scale(1.08)' : 'none',
                          filter: activeData.id >= 8 ? `drop-shadow(0 15px 35px ${activeData.glowColor})` : 'none',
                          transition: 'all 0.4s ease',
                        }}
                      />

                      {/* Scrim Overlay */}
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          background: activeData.id >= 8
                            ? 'radial-gradient(circle at center, transparent 40%, rgba(0,0,0,0.65) 100%)'
                            : 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.2) 40%, rgba(0,0,0,0.88) 100%)',
                          pointerEvents: 'none',
                        }}
                      />

                      {/* Floating Category Beacon Pill */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 14,
                          left: 14,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1.6,
                          py: 0.5,
                          borderRadius: '100px',
                          background: `${activeData.themeColor}dd`,
                          color: '#fff',
                          backdropFilter: 'blur(10px)',
                          boxShadow: `0 6px 18px ${activeData.glowColor}`,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem' }}>{activeData.icon}</Box>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.06em' }}>
                          {activeData.title} Studio
                        </Typography>
                      </Box>

                      {/* Floating Play / Action Target Icon */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: 54,
                          height: 54,
                          borderRadius: '50%',
                          background: `${activeData.themeColor}cc`,
                          backdropFilter: 'blur(10px)',
                          border: '2px solid rgba(255,255,255,0.7)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: `0 8px 30px ${activeData.glowColor}`,
                        }}
                      >
                        <PlayArrowRoundedIcon sx={{ fontSize: '1.9rem', color: '#fff', ml: 0.3 }} />
                      </Box>

                      {/* Bottom Feature Badges */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 12,
                          left: 14,
                          right: 14,
                          display: 'flex',
                          gap: 1,
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                          {activeData.showcasePills.map((pill) => (
                            <Box
                              key={pill}
                              sx={{
                                px: 1,
                                py: 0.3,
                                borderRadius: '4px',
                                background: 'rgba(0, 0, 0, 0.65)',
                                backdropFilter: 'blur(8px)',
                                border: '1px solid rgba(255,255,255,0.18)',
                                fontSize: '0.64rem',
                                color: '#ffffff',
                                fontFamily: 'monospace',
                                fontWeight: 700,
                              }}
                            >
                              {pill}
                            </Box>
                          ))}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                          <GraphicEqRoundedIcon sx={{ fontSize: '0.95rem', color: activeData.themeColor }} />
                          <Typography sx={{ color: '#ffffff', fontSize: '0.65rem', fontFamily: 'monospace', fontWeight: 700 }}>
                            LIVE CALIBRATED
                          </Typography>
                        </Box>
                      </Box>
                    </motion.div>
                  </AnimatePresence>
                </Box>

                {/* Dynamic Telemetry Metrics Dock (3 Columns) */}
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 2,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 1.5,
                    my: 1.8,
                  }}
                >
                  {activeData.metrics.map((m, i) => (
                    <Box
                      key={i}
                      sx={{
                        p: 1.2,
                        borderRadius: '12px',
                        background: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(0, 0, 0, 0.05)',
                        textAlign: 'center',
                      }}
                    >
                      <Typography sx={{ fontSize: '0.64rem', fontFamily: 'monospace', color: isDarkMode ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
                        {m.label}
                      </Typography>
                      <Typography sx={{ fontSize: '0.92rem', fontFamily: '"Inter", sans-serif', fontWeight: 800, color: activeData.themeColor }}>
                        {m.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Bottom Action Footer with Direct Route Link & Progress Bar */}
                <Box sx={{ position: 'relative', zIndex: 2, mt: 1 }}>
                  <Typography sx={{ color: isDarkMode ? '#cbd5e1' : '#334155', fontSize: '0.86rem', lineHeight: 1.5, mb: 2 }}>
                    {activeData.desc}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Button
                      component={Link}
                      to={activeData.route}
                      endIcon={<ArrowForwardRoundedIcon sx={{ transition: 'transform 0.25s ease' }} />}
                      sx={{
                        px: 3,
                        py: 1.1,
                        borderRadius: '14px',
                        background: activeData.themeColor,
                        color: '#ffffff',
                        fontWeight: 800,
                        fontSize: '0.85rem',
                        letterSpacing: '0.04em',
                        textTransform: 'none',
                        boxShadow: `0 8px 25px ${activeData.glowColor}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: activeData.themeColor,
                          filter: 'brightness(1.15)',
                          transform: 'translateY(-2px)',
                          boxShadow: `0 12px 35px ${activeData.glowColor}`,
                          '& .MuiButton-endIcon': { transform: 'translateX(5px)' },
                        },
                      }}
                    >
                      Launch {activeData.title} Studio
                    </Button>

                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: isDarkMode ? '#64748b' : '#94a3b8' }}>
                      Auto-playing Slide
                    </Typography>
                  </Box>

                  {/* Auto-Slide Progress Bar */}
                  <Box
                    sx={{
                      width: '100%',
                      height: '3px',
                      borderRadius: '2px',
                      background: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                      mt: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <motion.div
                      key={activeSlideIndex}
                      initial={{ width: '0%' }}
                      animate={{ width: '100%' }}
                      transition={{ duration: 4.2, ease: 'linear' }}
                      style={{
                        height: '100%',
                        background: activeData.themeColor,
                        boxShadow: `0 0 10px ${activeData.themeColor}`,
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        ) : (
          /* View Mode 2: Full Responsive Uniform Dynamic Grid Card Deck - Zero Blank Dead Space */
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: gridCards.length === 3 ? 'repeat(3, 1fr)' : gridCards.length === 2 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                lg: gridCards.length <= 4 ? `repeat(${gridCards.length}, 1fr)` : 'repeat(3, 1fr)',
                xl: 'repeat(4, 1fr)',
              },
              gap: 3,
              alignItems: 'stretch',
              width: '100%',
            }}
          >
            {gridCards.map((data) => {
              const actualIdx = INDUSTRY_DATA.findIndex((it) => it.id === data.id);
              const isSelected = activeSlideIndex === actualIdx;
              return (
                <Box
                  id={'cinema-card-' + data.id}
                  key={data.id}
                  onClick={() => setActiveSlideIndex(actualIdx)}
                  component={motion.div}
                  whileHover={{ y: -6, scale: 1.01 }}
                  transition={{ duration: 0.25 }}
                  sx={{
                    cursor: 'pointer',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                  }}
                >
                  <IndustryCard data={data} isDarkMode={isDarkMode} isSelected={isSelected} />
                </Box>
              );
            })}
          </Box>
        )}

      </Container>
    </Box>
  );
}

// Reusable Industry Card Component
const IndustryCard = ({ data, isDarkMode, isSelected }) => {
  return (
    <Box
      sx={{
        position: 'relative',
        background: isDarkMode
          ? isSelected
            ? 'rgba(255, 255, 255, 0.07)'
            : 'rgba(255, 255, 255, 0.03)'
          : isSelected
          ? '#ffffff'
          : '#ffffff',
        borderRadius: '24px',
        overflow: 'hidden',
        border: isSelected
          ? `2px solid ${data.themeColor}`
          : isDarkMode
          ? '1px solid rgba(255, 255, 255, 0.08)'
          : '1px solid rgba(0, 0, 0, 0.06)',
        boxShadow: isSelected
          ? `0 20px 50px ${data.glowColor}, inset 0 0 20px ${data.glowColor}15`
          : isDarkMode
          ? '0 12px 40px rgba(0,0,0,0.3)'
          : '0 12px 35px rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        p: 2.8,
        transition: 'all 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '18%',
          right: '18%',
          height: '3.5px',
          background: data.themeColor,
          borderRadius: '0 0 4px 4px',
          opacity: 0.8,
          transition: 'all 0.35s ease',
          zIndex: 3,
        },
        '&:hover': {
          transform: 'translateY(-7px)',
          borderColor: data.themeColor,
          boxShadow: `0 18px 45px ${data.glowColor}, 0 0 20px ${data.glowColor}25`,
          '&::before': {
            left: '5%',
            right: '5%',
            height: '4px',
            boxShadow: `0 0 14px ${data.themeColor}`,
            opacity: 1,
          },
        },
      }}
    >
      {/* Background Graphic positioned tastefully at bottom right with zero text collision */}
      <Box
        component={motion.img}
        src={data.image}
        alt={data.title}
        animate={isSelected ? {
          y: [0, -6, 0],
          scale: [1, 1.06, 1],
        } : {}}
        transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
        sx={{
          position: 'absolute',
          right: { xs: 4, sm: 8 },
          bottom: { xs: 8, sm: 12 },
          width: { xs: '38%', sm: '36%' },
          height: { xs: '38%', sm: '36%' },
          objectFit: 'contain',
          zIndex: 1,
          opacity: isSelected ? (isDarkMode ? 0.55 : 0.42) : (isDarkMode ? 0.28 : 0.2),
          pointerEvents: 'none',
          filter: isSelected ? `drop-shadow(0 8px 20px ${data.glowColor})` : 'none',
          transition: 'all 0.4s ease',
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Top Header: Icon & Category Badge with dynamic animation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.6 }}>
          <Box
            component={motion.div}
            animate={isSelected ? {
              scale: [1, 1.12, 1],
              rotate: [0, 5, -5, 0],
            } : {
              scale: 1,
              rotate: 0,
            }}
            transition={{
              duration: 2.8,
              repeat: isSelected ? Infinity : 0,
              ease: 'easeInOut',
            }}
            whileHover={{ scale: 1.14, rotate: 6 }}
            sx={{
              width: 52,
              height: 52,
              borderRadius: '16px',
              background: data.themeColor,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 8px 24px ${data.glowColor}`,
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '-50%',
                left: '-50%',
                width: '200%',
                height: '200%',
                background: 'linear-gradient(45deg, transparent 40%, rgba(255,255,255,0.4) 50%, transparent 60%)',
                transform: 'rotate(25deg)',
                animation: 'iconShimmer 3s infinite',
              },
              '@keyframes iconShimmer': {
                '0%': { transform: 'translateX(-100%) rotate(25deg)' },
                '100%': { transform: 'translateX(100%) rotate(25deg)' },
              },
            }}
          >
            {data.icon}
          </Box>

          {data.badge && (
            <Box
              component={motion.div}
              animate={{ y: [0, -2, 0] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.6,
                background: data.themeColor,
                color: '#fff',
                px: 1.4,
                py: 0.4,
                borderRadius: '20px',
                fontSize: '0.68rem',
                fontWeight: 800,
                letterSpacing: '0.06em',
                boxShadow: `0 4px 14px ${data.glowColor}`,
              }}
            >
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: '#fff', boxShadow: '0 0 6px #fff' }} />
              {data.badge}
            </Box>
          )}
        </Box>

        {/* Category micro-tag */}
        {data.category && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.1,
              py: 0.3,
              borderRadius: '6px',
              background: `${data.themeColor}18`,
              border: `1px solid ${data.themeColor}35`,
              color: data.themeColor,
              fontSize: '0.65rem',
              fontWeight: 800,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              mb: 0.8,
              width: 'fit-content',
            }}
          >
            {data.category}
          </Box>
        )}

        {/* Title & Description with distinct subtitle highlight */}
        <Typography variant="h5" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a', mb: 0.3, fontSize: '1.25rem', letterSpacing: '-0.01em' }}>
          {data.title}
        </Typography>
        {data.subtitle && (
          <Typography
            sx={{
              color: isDarkMode ? '#38bdf8' : '#0284c7',
              fontSize: '0.74rem',
              fontWeight: 700,
              mb: 1,
              lineHeight: 1.35,
            }}
          >
            {data.subtitle}
          </Typography>
        )}

        {/* Featured Key Metric Pill */}
        {data.metrics && data.metrics[0] && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.8,
              px: 1.2,
              py: 0.35,
              borderRadius: '8px',
              background: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
              border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)',
              mb: 1.5,
              width: 'fit-content',
            }}
          >
            <Typography sx={{ fontSize: '0.68rem', color: isDarkMode ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
              {data.metrics[0].label}:
            </Typography>
            <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, color: data.themeColor, fontFamily: 'monospace' }}>
              {data.metrics[0].value}
            </Typography>
          </Box>
        )}

        <Typography sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.8rem', lineHeight: 1.55, mb: 2, minHeight: 38, maxWidth: '100%' }}>
          {data.desc}
        </Typography>

        {/* Bullets List */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, mb: 2.5, flexGrow: 1 }}>
          {data.bullets.slice(0, 4).map((bullet, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <CheckCircleIcon sx={{ color: data.themeColor, fontSize: '0.95rem' }} />
              <Typography sx={{ color: isDarkMode ? '#e2e8f0' : '#334155', fontSize: '0.76rem', fontWeight: 600 }}>
                {bullet}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Action Link Button */}
        <Button
          component={Link}
          to={data.route || '#'}
          variant="contained"
          endIcon={<ArrowForwardIosIcon sx={{ fontSize: '0.72rem !important', transition: 'transform 0.2s ease' }} />}
          sx={{
            background: data.themeColor,
            color: '#fff',
            borderRadius: '20px',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.76rem',
            alignSelf: 'flex-start',
            px: 2.2,
            py: 0.65,
            boxShadow: `0 6px 18px ${data.glowColor}`,
            textDecoration: 'none',
            '&:hover': {
              background: data.themeColor,
              filter: 'brightness(1.15)',
              transform: 'translateY(-2px)',
              boxShadow: `0 10px 24px ${data.glowColor}`,
              '& .MuiButton-endIcon': { transform: 'translateX(4px)' },
            },
          }}
        >
          {data.btnText}
        </Button>
      </Box>
    </Box>
  );
};
